import React from 'react';
import { useQuery } from '@apollo/client';
import { Category, Company, Kpi, Product, TChartFilters } from '../models';
import {
  GET_ACTIVE_CLIENTS,
  GET_TIME_PERIOD_ALLY_SALES,
  GET_TIME_PERIOD_AVERAGE_TICKET,
  GET_TIME_PERIOD_CATEGORY_SALES,
  GET_TIME_PERIOD_ORDERS,
  GET_TIME_PERIOD_PRODUCT_SALES,
  GET_TOP_SELLING_PRODUCTS,
} from '../graphql/queries';

interface UseAdminDashboard {
  kpisInfo: {
    kpis: Kpi[];
    loading: boolean;
  };
  periodCategorySales: {
    data: {
      category: Category;
      sales: number;
    }[];
    loading: boolean;
  };
  periodAllySales: {
    data: {
      company: Company;
      sales: number;
    }[];
    loading: boolean;
  };
  periodTopProducts: {
    data: {
      product: Product;
      amount: number;
    }[];
    loading: boolean;
  };
}

export function useAdminDashboard(
  filters: Partial<TChartFilters>
): UseAdminDashboard {
  const {
    data: activeClientsData,
    loading: activeClientsLoading,
    error: activeClientsError,
  } = useQuery<{
    getActiveClients: { amount: number };
  }>(GET_ACTIVE_CLIENTS, {
    variables: {
      data: {
        endDate: filters?.endDate,
        startDate: filters?.startDate,
        timePeriod: filters?.timeScale,
        ...(filters?.allyId !== '' ? { allyId: filters.allyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: periodOrdersData,
    loading: periodOrdersLoading,
    error: periodOrdersError,
  } = useQuery<{
    getTimePeriodOrders: { difference: number; total: number };
  }>(GET_TIME_PERIOD_ORDERS, {
    variables: {
      data: {
        endDate: filters?.endDate,
        startDate: filters?.startDate,
        timePeriod: filters?.timeScale,
        ...(filters?.allyId !== '' ? { allyId: filters.allyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: averageTicketData,
    loading: averageTicketLoading,
    error: averageTicketError,
  } = useQuery<{
    getTimePeriodAverageTicket: { difference: number; total: number };
  }>(GET_TIME_PERIOD_AVERAGE_TICKET, {
    variables: {
      data: {
        endDate: filters?.endDate,
        startDate: filters?.startDate,
        timePeriod: filters?.timeScale,
        ...(filters?.allyId !== '' ? { allyId: filters.allyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: productSalesData,
    loading: productSalesLoading,
    error: productSalesError,
  } = useQuery<{
    getTimePeriodProductSales: { difference: number; total: number };
  }>(GET_TIME_PERIOD_PRODUCT_SALES, {
    variables: {
      data: {
        endDate: filters?.endDate,
        startDate: filters?.startDate,
        timePeriod: filters?.timeScale,
        ...(filters?.allyId !== '' ? { allyId: filters.allyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: alliesTopData,
    loading: alliesTopLoading,
    error: alliesTopError,
  } = useQuery<{
    getTimePeriodAllySales: {
      topAllies: {
        company: Company;
        sales: number;
      }[];
    };
  }>(GET_TIME_PERIOD_ALLY_SALES, {
    variables: {
      data: {
        endDate: filters?.endDate,
        startDate: filters?.startDate,
        timePeriod: filters?.timeScale,
        ...(filters?.allyId !== '' ? { allyId: filters.allyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: categorySalesData,
    loading: categorySalesLoading,
    error: categoriesSaleError,
  } = useQuery<{
    getTimePeriodCategorySales: {
      topCategories: {
        category: Category;
        sales: number;
      }[];
    };
  }>(GET_TIME_PERIOD_CATEGORY_SALES, {
    variables: {
      data: {
        endDate: filters?.endDate,
        startDate: filters?.startDate,
        timePeriod: filters?.timeScale,
        ...(filters?.allyId !== '' ? { allyId: filters.allyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: TopProductsData,
    loading: TopProductsLoading,
    error: TopProductsError,
  } = useQuery<{
    getTopSellingProducts: {
      topProducts: {
        product: Product;
        amount: number;
      }[];
    };
  }>(GET_TOP_SELLING_PRODUCTS, {
    variables: {
      data: {
        endDate: filters?.endDate,
        startDate: filters?.startDate,
        timePeriod: filters?.timeScale,
        ...(filters?.allyId !== '' ? { allyId: filters.allyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const buildKpis = (): Kpi[] => {
    const activeClients = {
      total: activeClientsData?.getActiveClients?.amount || 0,
      title: 'Clientes activos',
      differenceTitle: '',
    };

    const periodOrders = {
      title: 'Ordenes',
      total: periodOrdersData?.getTimePeriodOrders?.total || 0,
      difference: Math.round(
        periodOrdersData?.getTimePeriodOrders?.difference || 0
      ),
      differenceTitle: `vs. período anterior `,
    };

    const periodProductSales = {
      title: 'Ventas de productos',
      total: productSalesData?.getTimePeriodProductSales?.total || 0,
      difference: productSalesData?.getTimePeriodProductSales?.difference || 0,
      differenceTitle: `vs. período anterior `,
    };

    const periodAverageTicket = {
      title: 'Ticket promedio',
      total: averageTicketData?.getTimePeriodAverageTicket?.total || 0,
      difference:
        Math.round(averageTicketData?.getTimePeriodAverageTicket?.difference) ||
        0,
      differenceTitle: `vs. período anterior `,
    };

    return [
      activeClients,
      periodOrders,
      periodProductSales,
      periodAverageTicket,
    ];
  };

  const kpis = React.useMemo(() => {
    const _loading =
      activeClientsLoading ||
      periodOrdersLoading ||
      averageTicketLoading ||
      productSalesLoading;

    if (!_loading) {
      return buildKpis();
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeClientsLoading,
    periodOrdersLoading,
    averageTicketLoading,
    productSalesLoading,
  ]);

  return {
    kpisInfo: {
      kpis,
      loading:
        activeClientsLoading ||
        periodOrdersLoading ||
        averageTicketLoading ||
        productSalesLoading,
    },
    periodAllySales: {
      data: alliesTopData?.getTimePeriodAllySales?.topAllies || [],
      loading: alliesTopLoading,
    },
    periodCategorySales: {
      data: categorySalesData?.getTimePeriodCategorySales?.topCategories || [],
      loading: categorySalesLoading,
    },
    periodTopProducts: {
      data: TopProductsData?.getTopSellingProducts?.topProducts || [],
      loading: TopProductsLoading,
    },
  };
}
